import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { getResponseMessage } from "../../../utils/handlers/get-response-message"
import { getDashboardCatalog } from "../../DashboardPage/api/services.ts"
import type {
  ResolvedWidget,
  WidgetCatalog
} from "../../DashboardPage/redux/widgets.types.ts"
import * as services from "../api/services.ts"
import type { ReportDefinitionV1 } from "../types/canvas.types.ts"
import { reportCanvasSliceName } from "./reportCanvas.constants.ts"
import type { ReportCanvasState } from "./reportCanvas.types.ts"

const unwrap = <T>(data: unknown): T | undefined => {
  const enveloped = (data as { details?: { data?: T } } | undefined)?.details
    ?.data
  return enveloped ?? (data as T | undefined)
}

export const fetchReportCatalog = createAsyncThunk<WidgetCatalog | null>(
  `${reportCanvasSliceName}/fetchCatalog`,
  async (_, { rejectWithValue }) => {
    const response = await getDashboardCatalog()
    if (!response.ok) return rejectWithValue(response.data)
    return unwrap<WidgetCatalog>(response.data) ?? null
  }
)

export interface ResolveReportWidgetArg {
  widgetId: string
  type: string
  dataSource: string
  config: Record<string, unknown>
  params: Record<string, string>
}

export const resolveReportWidget = createAsyncThunk<
  | { widgetId: string; resolved: ResolvedWidget }
  | { widgetId: string; failed: true },
  ResolveReportWidgetArg
>(
  `${reportCanvasSliceName}/resolveWidget`,
  async ({ widgetId, type, dataSource, config, params }) => {
    const response = await services.postResolveWidget(
      { type, dataSource, config },
      params
    )
    if (!response.ok) return { widgetId, failed: true as const }
    const resolved = unwrap<ResolvedWidget>(response.data)
    if (!resolved || typeof resolved.type !== "string")
      return { widgetId, failed: true as const }
    return { widgetId, resolved }
  }
)

export interface SaveReportCanvasArg {
  reportId: string
  definition?: ReportDefinitionV1
  dirtySeq?: number
}

export const saveReportCanvas = createAsyncThunk<
  { dirtySeq: number; definition: ReportDefinitionV1 },
  SaveReportCanvasArg
>(
  `${reportCanvasSliceName}/save`,
  async (arg, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as { reportCanvas?: ReportCanvasState })
      .reportCanvas
    const definition = arg.definition ?? state?.definition
    if (!definition)
      return rejectWithValue({ message: "Report canvas is not loaded." })

    const response = await services.updateReport(arg.reportId, { definition })
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue({
        message: getResponseMessage(response.data, "Failed to save report.")
      })
    }
    return { dirtySeq: arg.dirtySeq ?? state?.dirtySeq ?? 0, definition }
  }
)
